const SECTION_TARGETS = ["technical-performance", "product-description"];
class CompareProductCard extends HTMLElement {
  constructor() {
    super();
    this.index = this.getAttribute("data-index") || 0;
    const savedProducts = localStorage.getItem("compare-products");
    if (savedProducts) {
      try {
        this.product = JSON.parse(savedProducts)[this.index];
        document.querySelector(".compare-products-widget")?.setAttribute("data-items", JSON.parse(savedProducts).length);
      } catch (error) {
        this.product = null;
      }
    } else {
      this.product = null;
    }

    this.onCompareProductsUpdated = (event) => {
      const handle = event.detail.compareProducts[this.index]?.handle;
      this.fetchAndRender(handle);
    };
  }

  connectedCallback() {
    this.fetchAndRender(this.product?.handle);
    document.addEventListener(
      "compare-products-updated",
      this.onCompareProductsUpdated
    );

    // Inject remove button
    this.injectRemoveButton();

    // Add event listener for close button
    this.addEventListener("click", (event) => {
      if (event.target.closest(".compare-product-remove")) {
        this.removeProduct();
      }
    });
  }

  disconnectedCallback() {
    document.removeEventListener(
      "compare-products-updated",
      this.onCompareProductsUpdated
    );
  }

  fetchAndRender(handle) {
    if (!handle) {
      this.renderEmpty();
      return;
    }

    const url = `/products/${handle}?view=compare-product-card`;
    fetch(url)
      .then(async (response) => {
        const contentType = response.headers.get("content-type") || "";
        if (contentType.includes("application/json")) {
          const json = await response.json();
          return json?.main || "";
        }
        return response.text();
      })
      .then((html) => {
        if (!html) {
          this.renderEmpty();
          return;
        }

        const parser = new DOMParser();
        const doc = parser.parseFromString(html, "text/html");
        const main_section = doc.querySelector(".compare-product-information");
        
        if (main_section) {
          this.innerHTML = main_section.outerHTML;
          
          // Inject remove button after rendering content
          this.injectRemoveButton();
        } else {
          this.innerHTML = "";
        }

        const sectionWrappers = doc.querySelectorAll(".section-wrapper");

        sectionWrappers.forEach((sectionWrapper, index) => {
          const target = document.querySelector(
            `.${SECTION_TARGETS[index]}-${this.index}`
          );
          if (target) {
            target.innerHTML = sectionWrapper ? sectionWrapper.outerHTML : "";
          }
        });
      })
      .catch(() => {
        this.renderEmpty();
      });
  }

  renderEmpty() {
    this.innerHTML = "";
  }

  injectRemoveButton() {
    // Only inject if button doesn't exist and we have a product handle
    if (!this.querySelector(".compare-product-remove") && this.product?.handle) {
      const removeButton = document.createElement("button");
      removeButton.className = "compare-product-remove";
      removeButton.setAttribute("data-product-handle", this.product.handle);
      removeButton.setAttribute("aria-label", "Remove product from comparison");
      
      // Get SVG URL from data attribute
      const svgUrl = this.getAttribute("data-icon-x-url");
      
      // Create img element with SVG source
      const img = document.createElement("img");
      img.src = svgUrl || "/assets/icon-x.svg"; // fallback to default path
      img.alt = "Remove";
      img.width = "12";
      img.height = "12";
      
      removeButton.appendChild(img);
      this.appendChild(removeButton);
    }
  }

  removeProduct() {
    // Get the product handle from the button's data attribute
    const removeButton = this.querySelector(".compare-product-remove");
    const productHandle = removeButton?.getAttribute("data-product-handle");

    if (!productHandle) return;

    // Get the current index of this card
    const currentIndex = parseInt(this.getAttribute("data-index"));

    // Get current products from localStorage
    let compareProducts = JSON.parse(
      localStorage.getItem("compare-products") || "[]"
    );

    // Find the index of the product to remove in the array
    const productIndex = compareProducts.findIndex(
      (product) => product.handle === productHandle
    );

    if (productIndex === -1) return;

    // Remove the product from the array
    compareProducts.splice(productIndex, 1);
    localStorage.setItem("compare-products", JSON.stringify(compareProducts));

    // Shift remaining products to fill the gap
    this.shiftProductsAndSections(compareProducts);

    // Clean the targeted card
    this.cleanTargetedCard(currentIndex);

    // Dispatch event to update all components
    document.dispatchEvent(
      new CustomEvent("compare-products-updated", {
        detail: {
          compareProducts: compareProducts,
        },
      })
    );
  }

  clearSectionContent(index) {
    // Clear technical performance section
    const techPerformanceSection = document.querySelector(
      `.technical-performance-${index}`
    );
    if (techPerformanceSection) {
      techPerformanceSection.innerHTML = "";
    }

    // Clear product description section
    const productDescriptionSection = document.querySelector(
      `.product-description-${index}`
    );
    if (productDescriptionSection) {
      productDescriptionSection.innerHTML = "";
    }
  }

  clearAllSectionContent() {
    // Clear all technical performance sections
    for (let i = 0; i < 3; i++) {
      const techPerformanceSection = document.querySelector(
        `.technical-performance-${i}`
      );
      if (techPerformanceSection) {
        techPerformanceSection.innerHTML = "";
      }
    }

    // Clear all product description sections
    for (let i = 0; i < 3; i++) {
      const productDescriptionSection = document.querySelector(
        `.product-description-${i}`
      );
      if (productDescriptionSection) {
        productDescriptionSection.innerHTML = "";
      }
    }
  }

  shiftProductsAndSections(compareProducts) {
    // Clear all cards and sections first
    for (let i = 0; i < 3; i++) {
      const card = document.querySelector(`compare-product-card[data-index="${i}"]`);
      if (card) {
        card.innerHTML = "";
      }
      this.clearSectionContent(i);
    }

    // Re-render products in their new positions
    document.querySelector(".compare-products-widget")?.setAttribute("data-items", compareProducts.length);
    compareProducts.forEach((product, newIndex) => {
      const card = document.querySelector(`compare-product-card[data-index="${newIndex}"]`);
      if (card && card.fetchAndRender) {
        card.fetchAndRender(product.handle);
      }
    });
  }

  cleanTargetedCard(index) {
    // Clean the specific card that was targeted for removal
    const card = document.querySelector(`compare-product-card[data-index="${index}"]`);
    if (card) {
      card.innerHTML = "";
    }
    
    // Clear the corresponding section content
    this.clearSectionContent(index);
  }
}
customElements.define("compare-product-card", CompareProductCard);

class CompareProductsButton extends HTMLElement {
  constructor() {
    super();
    this.cardItemSelector = this.getAttribute("card-item-selector");
    this.cardItem = this.closest(this.cardItemSelector);
    this.product = JSON.parse(
      this.cardItem.querySelector(".product-card-json").textContent.trim()
    );
    this.input = this.querySelector("input");
  }

  connectedCallback() {
    if (!this.product) {
      console.error(
        "No product found, please check the card item selector and ensure you have a product-card-json element in the card item"
      );
      return;
    }

    this.validateProductIsInLocalStorage();

    this.input.addEventListener("change", () => {
      this.updateLocalStorage();
    });
  }

  validateProductIsInLocalStorage() {
    const compareProducts = JSON.parse(
      localStorage.getItem("compare-products")
    );
    if (
      compareProducts.find((product) => product.handle === this.product.handle)
    ) {
      this.input.checked = true;
    } else {
      this.input.checked = false;
    }

    if (compareProducts.length >= 3 && !this.input.checked) {
      this.input.disabled = true;
    } else {
      this.input.disabled = false;
    }
  }

  updateLocalStorage() {
    let compareProducts = JSON.parse(localStorage.getItem("compare-products"));
    if (this.input.checked && compareProducts.length < 3) {
      compareProducts.push(this.product);
    } else {
      compareProducts = compareProducts.filter(
        (product) => product.handle !== this.product.handle
      );
    }

    localStorage.setItem("compare-products", JSON.stringify(compareProducts));

    document.dispatchEvent(
      new CustomEvent("compare-products-updated", {
        detail: {
          compareProducts: compareProducts,
        },
      })
    );

    if (compareProducts.length >= 3 && !this.input.checked) {
      this.input.disabled = true;
    } else {
      this.input.disabled = false;
    }
  }
}
customElements.define("compare-products-button", CompareProductsButton);

class CompareProductsModal extends HTMLElement {
  constructor() {
    super();
    this.clearAllButton = this.querySelector("#clear-all-button");
    this.compareProductsCount = this.querySelector(".compare-products-count");
    this.closeButton = this.querySelector("#modal-close-button");
    this.modalContent = this.querySelector(".compare-modal-content");
    this.overlay = this.querySelector(".modal-overlay");
  }

  connectedCallback() {
    this.updateCompareProductsCount();

    this.clearAllButton.addEventListener("click", () => {
      this.clearAll();
    });

    this.closeButton.addEventListener("click", () => {
      this.classList.toggle("active");
      this.setStyle();
    });

    this.overlay.addEventListener("click", () => {
      this.classList.remove("active");
      this.setStyle();
    });

    document.addEventListener("compare-products-updated", (event) => {
      this.updateCompareProductsCount();
      this.setStyle();
    });

    document.addEventListener("resize", () => {
      this.setStyle();
    });

    this.setStyle();
  }

  setStyle() {
    if (this.classList.contains("active")) {
      this.style.bottom = "0px";
    } else {
      const modalContentHeight = this.modalContent.offsetHeight + 10;
      console.log("modalContentHeight", modalContentHeight);
      this.style.bottom = `-${modalContentHeight}px`;
    }
  }

  clearAll() {
    localStorage.setItem("compare-products", JSON.stringify([]));
    document.dispatchEvent(
      new CustomEvent("compare-products-updated", {
        detail: {
          compareProducts: [],
        },
      })
    );
  }

  updateCompareProductsCount() {
    const compareProducts = JSON.parse(
      localStorage.getItem("compare-products")
    );
    this.compareProductsCount.textContent = `(${compareProducts.length}/3)`;
  }
}
customElements.define("compare-products-modal", CompareProductsModal);

class CompareProductsModalItem extends HTMLElement {
  constructor() {
    super();
    this.index = this.getAttribute("data-index");
    this.closeButton = this.querySelector(".remove-product");
    this.productHandle = JSON.parse(localStorage.getItem("compare-products"))[
      this.index
    ]?.handle;
    this.productMetafields = JSON.parse(
      localStorage.getItem("compare-products")
    )[this.index]?.metafields;
    this.productCard = this.querySelector(".product-card");
    this.productCardImage = this.querySelector(".product-card-image");
    this.productCardOverlay = this.querySelector(".product-card-overlay");
  }

  connectedCallback() {
    this.closeButton.addEventListener("click", () => {
      this.removeProduct();
    });

    this.getProduct(this.productHandle);
  }

  getProduct(handle) {
    if (handle) {
      fetch(`/products/${handle}.js`)
        .then((response) => response.json())
        .then((data) => {
          this.product = { ...data, metafields: this.productMetafields };
          this.renderProduct();
        });
    } else {
      this.cleanCard();
    }
  }

  renderProduct() {
    if (this.product) {
      this.productCard.classList.remove("product-card-empty");
      this.productCardImage.innerHTML = `<img src="${this.product.media[0].src}" alt="${this.product.title}">`;
      this.productCardOverlay.innerHTML = `<span class="product-card-title">${this.product.title}</span>`;
    }
  }

  removeProduct() {
    // Get current products from localStorage
    let compareProducts = JSON.parse(localStorage.getItem("compare-products") || "[]");
    this.productHandle = compareProducts[this.index]?.handle;

    if (!this.productHandle) return;

    // Find the index of the product to remove in the array
    const productIndex = compareProducts.findIndex(
      (product) => product.handle === this.productHandle
    );

    if (productIndex === -1) return;

    // Remove the product from the array
    compareProducts.splice(productIndex, 1);
    localStorage.setItem("compare-products", JSON.stringify(compareProducts));

    // Shift remaining products to fill the gap
    // this.shiftModalProducts(compareProducts);

    // Clean the targeted card
    this.cleanTargetedModalCard();

    // Dispatch event to update all components
    document.dispatchEvent(
      new CustomEvent("compare-products-updated", {
        detail: {
          compareProducts: compareProducts,
        },
      })
    );
  }

  cleanCard() {
    this.productCard.classList.add("product-card-empty");
    this.productCardImage.innerHTML =
      '<div class="product-card-empty-text"><p>Add Product to Compare</p></div>';
    this.productCardOverlay.innerHTML = "";
  }

  shiftModalProducts(compareProducts) {
    // Clear all modal items first
    for (let i = 0; i < 3; i++) {
      const modalItem = document.querySelector(`compare-products-modal-item[data-index="${i}"]`);
      if (modalItem) {
        modalItem.cleanCard();
      }
    }

    // Re-render products in their new positions
    compareProducts.forEach((product, newIndex) => {
      const modalItem = document.querySelector(`compare-products-modal-item[data-index="${newIndex}"]`);
      if (modalItem && modalItem.getProduct) {
        modalItem.getProduct(product.handle);
      }
    });
  }

  cleanTargetedModalCard() {
    // Clean the specific modal card that was targeted for removal
    this.cleanCard();
  }
}
customElements.define("compare-products-modal-item", CompareProductsModalItem);

// EVENT LISTENERS
document.addEventListener("DOMContentLoaded", () => {
  const createLocalStorage = () => {
    if (!localStorage.getItem("compare-products")) {
      localStorage.setItem("compare-products", JSON.stringify([]));
    }
  };

  document.addEventListener("compare-products-updated", (event) => {
    document.querySelectorAll("compare-products-button").forEach((button) => {
      button.validateProductIsInLocalStorage();
    });

    document.querySelectorAll("compare-products-modal-item").forEach((item) => {
      item.getProduct(event.detail.compareProducts[item.index]?.handle);
    });

    document.querySelectorAll("feature-content-item").forEach((item) => {
      item.getProduct(event.detail.compareProducts[item.index]?.handle);
    });

    updateCompareProductsPage(event.detail.compareProducts);
  });

  createLocalStorage();
  updateCompareProductsPage(
    JSON.parse(localStorage.getItem("compare-products"))
  );

  // Initialize modal items with empty state if no products
  const compareProducts = JSON.parse(localStorage.getItem("compare-products"));
  if (compareProducts.length === 0) {
    document.querySelectorAll("compare-products-modal-item").forEach((item) => {
      item.cleanCard();
    });
  }
});

function updateCompareProductsPage(products) {
  const compareProductsPage = document.querySelector(
    ".compare-products-container"
  );
  if (compareProductsPage && products.length === 0) {
    compareProductsPage.classList.add("compare-container-empty");
  } else if (compareProductsPage && products.length > 0) {
    compareProductsPage.classList.remove("compare-container-empty");
  }
}
